import Slider from "react-slick";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import usePrice from "../../hooks/usePrice";
import useFormat from "../../hooks/useFormat";
import usePriceFormat from "../../hooks/usePriceFormat";
import { useProductVariations } from "../../hooks/useProductVariations";
import ProductSpec from "./ProductSpec";
import ProductDescription from "./ProductDescription";
import ProductVariationSelect from "./ProductVariationSelect";
import AddingProductToCartModal from "../../components/modals/AddingProductToCartModal";
import {
  addToCarts,
  setCart,
  setUser,
  fetchUserData,
  loadCartListDetails,
} from "../../redux/actions";
import RfqToast from "./RfqToast";
import AddToCartToast from "../toasts/AddToCartToast";
import { AnalyticsDataStore } from "../../hooks/analytics-data";

function getProductImages(product = {}) {
  if (!product?.product_variations) return [];

  const { images, product_variations } = product;

  const allImages = [...(images || [])];

  for (const variation of product_variations) {
    for (const varImage of variation?.images?.filter(Boolean) || []) {
      allImages.push({ ...varImage, variationId: variation.id });
    }
  }

  return allImages;
}

const SingleProduct = ({ product }) => {
  const { formatPrice } = useFormat();
  const { formatPrices } = usePriceFormat();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const calledRef = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locationList, setLocationList] = useState([]);

  const { variationOptions, onVariationSelect, selectedVariation } =
    useProductVariations(product);
  const { productPrice } = usePrice({
    product: product,
    variation: selectedVariation,
    quantity: 1,
  });
  const images = useMemo(() => getProductImages(product), [product]);
  const user = useSelector((state) => state?.user_data?.user_data) ?? {};
  const region = useSelector((state) => state.region.region);

  // increment & decrement
  const [quantity, setQuantity] = useState(1);
  const incrementQuantity = () => setQuantity(quantity + 1);
  const decrementQuantity = () =>
    setQuantity(quantity > 1 ? quantity - 1 : quantity);
  const handleQuantityInput = (value) => {
    const num = Number(value);
    if (num >= 1) {
      setQuantity(num);
    }
  };

  //toast
  const [isOpen, setIsOpen] = useState(false);
  const [addTocartIsOpen, setAddTocartIsOpen] = useState(false);

  const [mainImage, setMainImage] = useState(
    product?.jasani_image_url ?? images[0]?.files?.medium?.url
  );

  const settingsThumbs = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    focusOnSelect: true,
  };

  useEffect(() => {
    if (product && !calledRef.current) {
      const analyticsStore = new AnalyticsDataStore();
      analyticsStore.rootStore = {};
      analyticsStore.select_item(product, "select_item", region);
      calledRef.current = true;
    }
  }, [product, region]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    if (addTocartIsOpen) {
      const timer = setTimeout(() => {
        setAddTocartIsOpen(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, addTocartIsOpen]);

  const getDiscountPrice = (discount) => {
    const price = formatPrices(
      productPrice.price - (productPrice.price * discount) / 100
    );
    return price;
  };

  const isOutOfStock = () => {
    if (quantity > selectedVariation?.available_stock) {
      return true;
    }
    return selectedVariation?.available_stock <= 0;
  };

  const addToCart = async () => {
    //guest cart

    const toastId = "addToCartError";

    if (!user?.locations) {
      let cartId = localStorage.getItem("cartIdKey");

      if (!cartId) {
        cartId = Math.random().toString(36).substring(2, 10);
        localStorage.setItem("cartIdKey", cartId);
      }
      const locationId = cartId;
      try {
        const createCart = await addToCarts(
          selectedVariation,
          quantity,
          locationId,
          region,
          dispatch
        );

        if (createCart) {
          dispatch(setCart(createCart));
        }

        const analyticsStore = new AnalyticsDataStore();
        analyticsStore.rootStore = {};
        analyticsStore.add_to_cart(
          product,
          selectedVariation,
          "add_to_cart",
          quantity
        );

        setAddTocartIsOpen(true);
        return;
      } catch (error) {
        console.log(error);
      }

      return;
    } else if (user?.locations?.length === 0) {
      navigate("/self-service/mycompany/actions");
      if (!toast.isActive(toastId)) {
        toast.error(
          "Please add delivery location to add the product in carts.",
          { toastId }
        );
      }
      return;
    } else if (user?.locations?.length === 1) {
      const locationId = user?.locations[0]?.id || undefined;

      try {
        const createCart = await addToCarts(
          selectedVariation,
          quantity,
          locationId,
          region,
          dispatch
        );

        if (createCart) {
          dispatch(setCart(createCart));

          const analyticsStore = new AnalyticsDataStore();
          analyticsStore.rootStore = {};
          analyticsStore.add_to_cart(
            product,
            selectedVariation,
            "add_to_cart",
            quantity
          );

          const user = await fetchUserData();
          if (user) {
            dispatch(setUser(user));

            if (user?.shopping_carts) {
              await loadCartListDetails(user?.shopping_carts, dispatch);
            }
          }
          setAddTocartIsOpen(true);
          return;
        }
      } catch (error) {
        console.log(error);
      }
      return;
    } else if (user?.locations?.length > 1) {
      // multiple location - Select location of the cart
      setLocationList(user?.locations);
      setIsModalOpen(true);
    } else {
      console.warn("noo actions ");
    }
  };

  const handleStoreRFQ = useCallback(() => {
    const productImage =
      product?.jasani_image_url || images[0]?.files?.medium?.url;

    const rfq_data = {
      image: productImage,
      rfq_name: product?.name,
      price: productPrice?.price,
      code: selectedVariation?.sku,
      slug: product?.slug,
      productid: product?.id,
      variationid: product?.details?.id,
      quantity: quantity,
    };

    let existingRFQList = JSON.parse(localStorage.getItem("rfq_list")) || [];

    // Check if the product already exists based on 'code'
    const existingProductIndex = existingRFQList.findIndex(
      (item) => item.code === rfq_data.code
    );

    if (existingProductIndex !== -1) {
      // If the product exists, update its quantity
      existingRFQList[existingProductIndex] = {
        ...existingRFQList[existingProductIndex],
        quantity:
          existingRFQList[existingProductIndex].quantity + rfq_data.quantity,
      };
    } else {
      // If the product does not exist, add it to the list
      existingRFQList.push(rfq_data);
    }

    // Update state and local storage
    //setStoredRFQList(existingRFQList);
    localStorage.setItem("rfq_list", JSON.stringify(existingRFQList));
    window.dispatchEvent(new Event("rfqListUpdated"));

    setIsOpen(true); // Set popup open
  }, [product, images, productPrice, selectedVariation, quantity]);

  // comparison
  const comparison = useCallback(() => {
    const Image = product?.jasani_image_url || images[0]?.files?.medium?.url;

    const comparison_data = {
      image: Image,
      name: product?.name,
      brand: product?.brand[0]?.name,
      code: selectedVariation?.sku,
      price: productPrice?.price,
      unit: product?.unit,
      supplier: product?.supplier || "-",
      productvariations: product?.product_variations[0],
      url: product?.url,
    };
    const existingList =
      JSON.parse(localStorage.getItem("comparison_list")) || [];

    // 1. Check if item already exists
    const alreadyExists = existingList.some(
      (item) => item.code === comparison_data.code
    );

    if (alreadyExists) {
      toast.info("Product already exists in comparison list");
      return;
    }

    // 2. Check if list already has 4 items
    if (existingList.length >= 4) {
      toast.error("You can only add up to 4 items only");
      return;
    }

    // 3. Add new item
    existingList.push(comparison_data);
    localStorage.setItem("comparison_list", JSON.stringify(existingList));
    toast.success("Product added to comparison");

    // Optional: notify other tabs or components
    window.dispatchEvent(new Event("comparisonListUpdated"));
  }, [images, product, productPrice, selectedVariation]);

  //console.log("product?.tiers", product?.tiers);

  return (
    <>
      {/* selecting location of the cart */}
      {isModalOpen && (
        <AddingProductToCartModal
          onClose={() => setIsModalOpen(false)}
          locationList={locationList}
          product={product}
          productVariation={selectedVariation}
          quantity={quantity}
          region={region}
        />
      )}
      {/* product detail page */}
      <div className="col-lg-9">
        <div className="row gy-4">
          <div className="col-xl-6">
            <div className="product-details__left">
              <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                <div className="product-details__thumb flex-center h-100">
                  {selectedVariation && (
                    <div>
                      <img
                        src={mainImage}
                        alt="Main Product"
                        style={{
                          maxHeight: "500px",
                        }}
                      />
                      {/* {product?.jasani_image_url && (
                        <img
                          src={mainImage}
                          alt="Main Product"
                          style={{
                            maxHeight: "500px",
                          }}
                        />
                      )}
                      {!product?.jasani_image_url && (
                        <img
                          src={images[0]?.files?.medium?.url}
                          alt="Main Product"
                          style={{
                            maxHeight: "500px",
                          }}
                        />
                      )} */}
                    </div>
                  )}
                  {/* <img src={mainImage} alt="Main Product" /> */}
                </div>
              </div>
              <div className="mt-24">
                <div className="product-details__images-slider">
                  {selectedVariation && (
                    <Slider {...settingsThumbs}>
                      {images.map((image, index) => (
                        <div
                          className="center max-w-120 max-h-120 h-100 flex-center border border-gray-100 rounded-16 p-8"
                          key={index}
                          onClick={() =>
                            setMainImage(image?.files?.medium?.url)
                          }
                        >
                          <img
                            className="thum"
                            src={image?.files?.medium?.url}
                            alt={`Thumbnail ${index}`}
                            style={{
                              maxHeight: "100px",
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="product-details__content">
              <h5 className="prod_det_font mb-12">{product?.name}</h5>
              <div className="flex-align flex-wrap gap-12">
                {selectedVariation && (
                  <span className="text-gray-900">
                    <span className="text-gray-400">
                      SKU : {selectedVariation?.sku}{" "}
                    </span>
                  </span>
                )}
              </div>

              <span className="mt-16 text-gray-700 border-top border-gray-100 d-block" />

              <div className="mt-16 flex-between flex-wrap gap-32 w-full">
                <div className="flex items-center gap-4">
                  <h4
                    className={`mb-0 ${
                      productPrice?.salePrice
                        ? "text-decoration-line-through text-gray-500 prod_det_font"
                        : "prod_det_font"
                    }`}
                  >
                    {formatPrice(productPrice?.price)}
                  </h4>
                  {productPrice?.salePrice && (
                    <h4 className="mb-0">
                      {formatPrice(productPrice?.salePrice)}
                    </h4>
                  )}
                </div>

                {/* Button aligned to the right */}
                <Link
                  className="btn hover-bg-main-600 hover-text-white flex-align d-inline-flex gap-8 px-48"
                  onClick={() => handleStoreRFQ()}
                >
                  <i className="ph ph-clipboard-text" /> Request Quote
                </Link>
              </div>
              <span className="mt-16 mt-16 text-gray-700   d-block" />
              {product?.tiers?.length > 0 ? (
                <div className="gap-2 p-12 bg-light-grey border border-two-600 border-dashed rounded-8 mb-16  ">
                  {" "}
                  {/* flex-between */}
                  <div>
                    <div className="w-full ">
                      <div className=" mb-10">
                        <h5 className="prod_det_font mb-12">
                          Quantity discounts
                        </h5>
                      </div>
                      {/* Desktop View (only shown on md and larger) */}
                      <div className="d-none d-md-block">
                        <div className="gap-2">
                          <ul className="w-75">
                            {product?.tiers.slice(1).map((priceInfo, key) => (
                              <li
                                key={key}
                                className="d-flex justify-content-between mb-1"
                              >
                                <div className="text-left text-black">
                                  Buy {priceInfo.from}+
                                </div>
                                <div className="text-right text-black">
                                  {priceInfo.percentage}% OFF
                                </div>
                                <div className="text-right text-black">
                                  {getDiscountPrice(priceInfo.percentage)}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Mobile View (only shown on smaller than md) */}
                      <div className="d-block d-md-none">
                        <ul className="w-100 list-unstyled">
                          {product?.tiers.slice(1).map((priceInfo, key) => (
                            <li
                              key={key}
                              className="mb-2 d-flex text-center justify-content-between px-3"
                            >
                              <div className="w-33">Buy {priceInfo.from}+</div>
                              <div className="w-33">
                                {priceInfo.percentage}% OFF
                              </div>
                              <div className="w-33">
                                {getDiscountPrice(priceInfo.percentage)}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <span className="mt-16 pt-16 text-gray-700 border-top border-gray-100 d-block" />

              {/* quantity part */}
              <span className="text-gray-900 d-block mb-8">Quantity:</span>
              <div className="flex-between gap-16 flex-wrap w-full">
                <div className="flex-align flex-wrap gap-16">
                  <div className="d-flex rounded-4 overflow-hidden">
                    <button
                      type="button"
                      oonClick={decrementQuantity}
                      className="quantity__minus border border-end border-gray-100 flex-shrink-0 h-48 w-48 text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-minus" />
                    </button>
                    <input
                      type="number"
                      className="quantity__input flex-grow-1 border border-gray-100 border-start-0 border-end-0 text-center w-32 px-4"
                      min="1"
                      value={quantity}
                      onChange={(e) => handleQuantityInput(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={incrementQuantity}
                      className="quantity__plus border border-end border-gray-100 flex-shrink-0 h-48 w-48 text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                    >
                      <i className="ph ph-plus" />
                    </button>
                  </div>
                </div>
                <div className="ml-auto">
                  <Link
                    onClick={addToCart}
                    className="btn hover-bg-main-600 hover-text-white flex-align d-inline-flex gap-8 px-48"
                    disabled={
                      !selectedVariation ||
                      !selectedVariation?.base_gross_price ||
                      isOutOfStock()
                    }
                  >
                    <i className="ph ph-shopping-cart" /> Add To Cart
                    &nbsp;&nbsp;&nbsp;&nbsp;
                  </Link>
                </div>
              </div>

              <span className="mt-16 pt-16 text-gray-700 border-top border-gray-100 d-block" />
              <div className="flex-wrap gap-2 rounded-8 ">
                <div className="gap-2 mb-10 ">
                  <h6 className="prod_det_font mb-12">More options</h6>
                </div>
                <div className="gap-2 text-gray-900 ">
                  <ProductVariationSelect
                    variationOptions={variationOptions}
                    onSelect={onVariationSelect}
                  />
                </div>
              </div>
              {/* comparison */}
              <div className="flex-between gap-16 flex-wrap w-full mt-20">
                <div className="flex-align flex-wrap gap-16"></div>
                <div
                  className="ml-auto flex items-center text-xl  cursor-pointer me-6 mt-6"
                  onClick={comparison}
                >
                  <i className="text-black ph ph-arrows-left-right ml-1"></i>
                  <span className="text-black">&nbsp;Add to comparison</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ProductSpec */}
      {product && <ProductSpec />}

      {/* product Description */}
      {product && (
        <ProductDescription
          product={product}
          selectedVariation={selectedVariation}
        />
      )}

      <div>
        {isOpen && (
          <RfqToast
            product={product}
            images={images}
            formatPrice={formatPrice}
            productPrice={productPrice}
            setIsOpen={setIsOpen}
          />
        )}
        {addTocartIsOpen && (
          <AddToCartToast
            product={product}
            images={images}
            formatPrice={formatPrice}
            productPrice={productPrice}
            onClose={() => setAddTocartIsOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default SingleProduct;
